<template>
  <div v-if="question">
    <basic-question v-model="modelValue" :required="required" :titleProps="titleProps" disabled v-bind="question" />
  </div>
</template>

<script>
import { WatchOptionsWithHandler, Component } from 'vue'
import { PropsDefinition, Computed } from '@/_typedefs'
import { createNamespacedHelpers } from 'vuex'
import { TitleProps } from '../Title/TitleNode.vue'
import BasicQuestion from '../../Questions/Basic/BasicQuestion.vue'
import computedFunctions from '../../Questions/Computed/functions'

const { mapActions: newAttendanceFormActions, mapMutations: newAttendanceFormMutations } =
  createNamespacedHelpers('newAttendanceForm')

/**
 * @typedef {Object} Question
 * @property {string} title
 * @property {string} kind
 * @property {boolean} [custom]
 * @property {object} questionData
 */

/**
 * @typedef {Object} ComputedQuestionNodeProps
 * @property {TitleProps} titleProps
 * @property {boolean} required
 * @property {string} questionKey
 */

export default {
  components: { BasicQuestion },
  name: 'ComputedQuestionNode',
  /** @type {PropsDefinition<ComputedQuestionNodeProps>} */
  props: {
    titleProps: {
      type: Object,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    questionKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      /** @type {Question | null} */
      question: null,
      /** @type {any} */
      modelValue: null,
      /** @type {((...args: any[]) => any)?} */
      computedFn: null,
      /** @type {Map<string, any>} */
      computedArgs: new Map(),
      /** @type {Function?} */
      unwatch: null,
    }
  },
  methods: {
    ...newAttendanceFormActions(['getQuestion', 'getAnswer']),
    ...newAttendanceFormMutations(['updateAnswer']),
    openQuestion() {
      this.$emit('open-expandable-box')
    },
  },
  async created() {
    const [question, answer] = await Promise.all([this.getQuestion(this.questionKey), this.getAnswer(this.questionKey)])
    this.question = question
    this.modelValue = answer

    this.$eventBus.on(`open-question-${this.questionKey}`, this.openQuestion)

    const computedArgsKey = JSON.parse(this.question.questionData.computedArgs ?? '[]')
    const computedFnName = this.question.questionData.computedFn ?? ''

    this.computedFn = computedFunctions[computedFnName]

    this.unwatch = this.$store.subscribe((mutation) => {
      if (mutation.type !== 'newAttendanceForm/updateAnswer') {
        return
      }

      const { questionKey, value } = mutation.payload

      if (!this.computedArgs.has(questionKey)) {
        return
      }

      this.computedArgs.set(questionKey, value)

      this.modelValue = this.computedFn?.(...this.computedArgs.values())
    })

    this.$nextTick(async () => {
      for (const argKey of computedArgsKey) {
        this.computedArgs.set(argKey, await this.getAnswer(argKey))
      }

      this.modelValue = this.computedFn?.(...this.computedArgs.values())
    })


    this.$eventBus.emit(`question-${this.questionKey}-loaded`)

  },
  watch: {
    /** @type {WatchOptionsWithHandler<any>} */
    modelValue: {
      handler(value) {
        this.updateAnswer({ questionKey: this.questionKey, value })
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.unwatch?.()
    this.$eventBus.off(`open-${this.questionKey}`, this.openQuestion)
  },
}
</script>

<style></style>
